import React from "react";
import { motion } from "framer-motion";
import { LucideIcon } from "lucide-react";
import { useThemeMode } from "@/hooks/useThemeMode";
import { cn } from "@/lib/utils";

interface HolographicMetricProps {
  title: string;
  value: string | number;
  icon?: LucideIcon;
  glowColor?: "cyan" | "purple" | "pink" | "orange" | "green" | "blue";
  animated?: boolean;
  pulseEffect?: boolean;
}

export const HolographicMetric: React.FC<HolographicMetricProps> = ({
  title,
  value,
  icon: Icon,
  glowColor = "cyan",
  animated = true,
  pulseEffect = true,
}) => {
  const { isDark } = useThemeMode();

  const glowColors = {
    cyan: "#0ea5e9",
    purple: "#8b5cf6",
    pink: "#ec4899",
    orange: "#f59e0b",
    green: "#10b981",
    blue: "#3b82f6",
  };

  const color = glowColors[glowColor];

  return (
    <motion.div
      initial={animated ? { opacity: 0, scale: 0.8 } : undefined}
      animate={animated ? { opacity: 1, scale: 1 } : undefined}
      transition={{ duration: 0.6 }}
      className="relative p-6 rounded-2xl overflow-hidden group cursor-pointer"
      style={{
        background: isDark 
          ? `linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(30, 41, 59, 0.95))`
          : `linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.95))`,
        border: `1px solid ${color}60`,
        boxShadow: `0 0 30px ${color}25, inset 0 1px 0 ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.8)'}`,
      }}
      whileHover={{
        scale: 1.05,
        rotateY: 5,
        transition: { duration: 0.3 }
      }}
    >
      {/* Holographic scan line effect */}
      <motion.div
        animate={pulseEffect ? {
          y: ["-100%", "200%"],
          opacity: [0, 0.6, 0],
        } : undefined}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "linear",
        }}
        className="absolute inset-x-0 h-1 opacity-0"
        style={{
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          filter: "blur(1px)",
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -20, 0],
              x: [0, 10, 0],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              delay: i * 0.5,
            }}
            className="absolute w-1 h-1 rounded-full"
            style={{
              background: color,
              left: `${20 + i * 30}%`,
              top: `${30 + i * 20}%`,
              boxShadow: `0 0 10px ${color}`,
            }}
          />
        ))}
      </div>

      {/* Content with enhanced contrast */}
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <motion.h3 
            className={cn(
              "text-sm font-medium mb-2",
              isDark ? "text-slate-200" : "text-slate-700"
            )}
            initial={animated ? { opacity: 0, x: -10 } : undefined}
            animate={animated ? { opacity: 1, x: 0 } : undefined}
            transition={{ delay: 0.2 }}
          >
            {title}
          </motion.h3>
          
          {Icon && (
            <motion.div
              initial={animated ? { opacity: 0, scale: 0 } : undefined}
              animate={animated ? { opacity: 1, scale: 1 } : undefined}
              transition={{ delay: 0.4, type: "spring" }}
              className="p-2 rounded-lg"
              style={{
                background: `${color}25`,
                border: `1px solid ${color}50`,
              }}
            >
              <Icon className="w-4 h-4" style={{ color }} />
            </motion.div>
          )}
        </div>
        
        <motion.div
          initial={animated ? { opacity: 0, y: 10 } : undefined}
          animate={animated ? { opacity: 1, y: 0 } : undefined}
          transition={{ delay: 0.6 }}
          className="text-2xl font-bold"
          style={{ 
            color,
            textShadow: `0 0 20px ${color}50`,
            filter: isDark ? 'brightness(1.1)' : 'brightness(0.9)'
          }}
        >
          {value}
        </motion.div>
      </div>

      {/* Hover glow effect */}
      <motion.div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background: `radial-gradient(circle at center, ${color}15 0%, transparent 70%)`,
        }}
      />
    </motion.div>
  );
};
